/**
 * Fix default values service.
 * Fixed default values provided to prevent mismatch with data from api calls.
 * @module fixDefaults
 */

/**
 * Check if category provided in default values exist in data
 * @param {object} - data, category name to check
 * @return {boolean} -
 */
const checkCategoryExistance = ({ data, category }) => (data[category] ? true : false);

/**
 * Get first category from array
 * @param {array} categoryList - array of categories available
 * @return {string} - first category from the array provided
 */
const getFirstCategoryFromList = (categoryList) => categoryList[0];

/**
 * Get minimal value for amount or term
 * @param {object} data - data
 * @param {string} itemName - type of item, term or amount
 * @return {number} - min value
 */
const getMinTermOrAmount = (data, itemName) => {
    return data[getFirstCategoryFromList(Object.keys(data))][itemName].min;
};

/**
 * Prepare default values:
 * - transform keys to acceptable format
 * - set dateStart if no date provided or provided date is invalid
 * @param {object} - consists of default values
 * @return {object} - default values object, transformed and prepared
 */
const fixDefaults = (toBeFixed) => {
    const { data, defaultValues } = toBeFixed;
    const category = checkCategoryExistance({ data, category: defaultValues.category })
        ? defaultValues.category
        : getFirstCategoryFromList(Object.keys(data));
    const dateStartObj = new Date(defaultValues.dateStart);

    if (defaultValues.amount === null) {
        defaultValues.amount = getMinTermOrAmount(data, 'amount');
    }

    if (defaultValues.term === null) {
        defaultValues.term = getMinTermOrAmount(data, 'term');
    }

    // if default date equals an empty string or any other non valid data format
    if (!defaultValues.dateStart || isNaN(dateStartObj.getDate())) {
        defaultValues.dateStart = new Date();
    } else {
        defaultValues.dateStart = dateStartObj;
    }

    if (defaultValues.category !== category) {
        defaultValues.category = category;
    }

    if (typeof defaultValues.insurance === 'undefined' || defaultValues.insurance === null) {
        // "insurance" field is not nullable and should be always defined.
        defaultValues.insurance = false;
    }

    if (defaultValues.onHand === null) {
        defaultValues.onHand = false;
    }

    if (defaultValues.numberOfPayments === null) {
        defaultValues.numberOfPayments = 1;
    }

    toBeFixed.userInput = defaultValues;
    delete toBeFixed.defaultValues;

    return toBeFixed;
};

export { checkCategoryExistance, getFirstCategoryFromList, getMinTermOrAmount, fixDefaults };
